/**
 * MergeFieldToolbar - Dropdown for inserting merge field tokens into text inputs.
 *
 * Used in email template editors to insert personalization tokens like
 * {{first_name}}, {{company_name}}, etc.
 */
import { useTranslation } from 'react-i18next';
import { Braces } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';

interface MergeField {
  token: string;
  labelKey: string;
}

const CONTACT_FIELDS: MergeField[] = [
  { token: '{{first_name}}', labelKey: 'mergeFields.firstName' },
  { token: '{{last_name}}', labelKey: 'mergeFields.lastName' },
  { token: '{{full_name}}', labelKey: 'mergeFields.fullName' },
  { token: '{{email}}', labelKey: 'mergeFields.email' },
  { token: '{{job_title}}', labelKey: 'mergeFields.jobTitle' },
  { token: '{{phone}}', labelKey: 'mergeFields.phone' },
];

const COMPANY_FIELDS: MergeField[] = [
  { token: '{{company_name}}', labelKey: 'mergeFields.companyName' },
  { token: '{{company_domain}}', labelKey: 'mergeFields.companyDomain' },
  { token: '{{company_industry}}', labelKey: 'mergeFields.companyIndustry' },
];

const SENDER_FIELDS: MergeField[] = [
  { token: '{{sender_name}}', labelKey: 'mergeFields.senderName' },
  { token: '{{sender_email}}', labelKey: 'mergeFields.senderEmail' },
  { token: '{{sender_title}}', labelKey: 'mergeFields.senderTitle' },
];

interface MergeFieldToolbarProps {
  onInsert: (token: string) => void;
}

export function MergeFieldToolbar({ onInsert }: MergeFieldToolbarProps) {
  const { t } = useTranslation('pipeline');

  const renderGroup = (label: string, fields: MergeField[]) => (
    <>
      <DropdownMenuLabel className="text-xs">{label}</DropdownMenuLabel>
      {fields.map((field) => (
        <DropdownMenuItem key={field.token} onClick={() => onInsert(field.token)}>
          <code className="text-xs mr-2 text-muted-foreground">{field.token}</code>
          <span className="text-sm">{t(field.labelKey)}</span>
        </DropdownMenuItem>
      ))}
    </>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" type="button" className="gap-1.5">
          <Braces className="h-3.5 w-3.5" />
          {t('prospecting.insertMergeField')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {renderGroup(t('mergeFields.contactFields'), CONTACT_FIELDS)}
        <DropdownMenuSeparator />
        {renderGroup(t('mergeFields.companyFields'), COMPANY_FIELDS)}
        <DropdownMenuSeparator />
        {renderGroup(t('mergeFields.senderFields'), SENDER_FIELDS)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
